import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  AfterViewInit,
  computed,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Capacitor } from '@capacitor/core';
import { NGXLogger } from 'ngx-logger';
import { Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { VentaRapidaUserSearchService } from 'src/app/services/venta-rapida/venta-rapida-user-search.service';
import { VentaRapidaUser } from 'src/app/models/venta-rapida/venta-rapida-user.model';
import { AnonymousIntent } from 'src/app/models/venta-rapida/anonymous-intent.model';

@Component({
  selector: 'app-venta-rapida-search-step',
  templateUrl: './venta-rapida-search-step.component.html',
  styleUrls: ['./venta-rapida-search-step.component.scss'],
  standalone: false
})
export class VentaRapidaSearchStepComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input({ required: true }) eventId!: string;

  @Output() userSelected = new EventEmitter<VentaRapidaUser>();
  @Output() anonymousChosen = new EventEmitter<AnonymousIntent>();

  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLIonInputElement>;

  public form: FormGroup;

  searchQuery = signal<string>('');
  searchResults = signal<VentaRapidaUser[]>([]);
  lastSuccessfulResults = signal<VentaRapidaUser[]>([]);
  isLoading = signal<boolean>(false);
  isSearchPending = signal<boolean>(false);
  hasNetworkError = signal<boolean>(false);

  queryKind = computed<'dni' | 'name'>(() =>
    /\d/.test(this.searchQuery().trim()) ? 'dni' : 'name'
  );

  showNoResultsCard = computed(() =>
    !this.isLoading() &&
    !this.isSearchPending() &&
    !this.hasNetworkError() &&
    this.searchQuery().trim().length >= 3 &&
    this.searchResults().length === 0
  );

  public isNativePlatform = Capacitor.isNativePlatform();
  public isNumericKeyboard = false;

  private destroy$ = new Subject<void>();

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _search: VentaRapidaUserSearchService,
    private readonly _logger: NGXLogger,
  ) {
    this.form = this._fb.group({ query: [''] });
  }

  ngOnInit(): void {
    const queryCtrl = this.form.get('query');
    if (!queryCtrl) return;

    queryCtrl.valueChanges
      .pipe(
        distinctUntilChanged(),
        tap((value: string) => {
          this.searchQuery.set(value ?? '');
          const q = (value ?? '').trim();
          if (q.length === 0) {
            this.searchResults.set([]);
            this.lastSuccessfulResults.set([]);
            this.isSearchPending.set(false);
            this.isLoading.set(false);
            this.hasNetworkError.set(false);
          } else if (q.length > 0 && q.length < 3) {
            this.searchResults.set([]);
            this.isSearchPending.set(false);
            this.isLoading.set(false);
          } else {
            this.isSearchPending.set(true);
          }
        }),
        debounceTime(600),
        filter((value: string) => (value ?? '').trim().length >= 3),
        tap(() => {
          this.isLoading.set(true);
          this.isSearchPending.set(false);
          this.hasNetworkError.set(false);
          if (this.lastSuccessfulResults().length > 0) {
            this.searchResults.set([...this.lastSuccessfulResults()]);
          }
        }),
        switchMap((value: string) =>
          this._search.search(this.eventId, value.trim()).pipe(
            tap((response) => {
              this.searchResults.set(response.data);
              this.lastSuccessfulResults.set(response.data);
            }),
            catchError((err) => {
              this._logger.error('[VentaRapidaSearch] search failed', err);
              this.hasNetworkError.set(true);
              this.searchResults.set(this.lastSuccessfulResults());
              return of({ data: this.lastSuccessfulResults() });
            }),
            finalize(() => this.isLoading.set(false))
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.searchInputRef?.nativeElement?.setFocus?.();
    }, 150);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onUserSelected(user: VentaRapidaUser): void {
    this.userSelected.emit(user);
  }

  onAnonymousChosen(intent: AnonymousIntent): void {
    this.anonymousChosen.emit(intent);
  }

  onFullAnonymousShortcut(): void {
    this.anonymousChosen.emit({ kind: 'full-anonymous' });
  }

  toggleKeyboardType(): void {
    this.isNumericKeyboard = !this.isNumericKeyboard;
    setTimeout(() => {
      const inputEl = document.querySelector('ion-input input') as HTMLInputElement | null;
      inputEl?.focus();
    }, 100);
  }

  retry(): void {
    this.hasNetworkError.set(false);
    const q = this.searchQuery();
    if (q.trim().length >= 3) {
      this.form.get('query')?.setValue(q, { emitEvent: true });
    }
  }
}
